<script>
import { hexToRgb, textOnColor } from '@modules/global/utils/Color'
import IconNoModuleAccess from '@modules/global/components/icon-no-module-access/icon-no-module-access.vue'

export default {
  name: 'BaseToggleButton',
  components: { IconNoModuleAccess },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    buttonStyle: {
      type: String,
      default: 'flatWhite',
      validator: (value) =>
        ['flatGray', 'flatWhite', 'shadowWhite'].includes(value),
    },
    small: {
      type: Boolean,
      default: false,
    },
    // Permite desmarcar a opção ativa (clique no ativo volta a "nada selecionado").
    // Sem isto o `mandatory` do Vuetify torna esse clique um no-op, sem emitir.
    deselectable: {
      type: Boolean,
      default: false,
    },
    pill: {
      type: Boolean,
      default: false,
    },
    // Visual "segmented" (faixa cinza --neutral-100 + pílula ativa branca com
    // sombra), idêntico ao BaseSegmentedTabs. Usado na navegação por sub-abas.
    // Quando true, sobrescreve o ativo padrão (--primary) pela pílula branca.
    segmented: {
      type: Boolean,
      default: false,
    },
    // Separa os botões com gap (pílulas independentes). Só com `pill`.
    spaced: {
      type: Boolean,
      default: false,
    },
    pillBgFromStyle: {
      type: Boolean,
      default: false,
    },
    // Ativo que SALTA — a regra de estado selecionado da casa: borda de 2px na cor da
    // marca + fundo tingido + sombra + peso 600. Para escolhas que mudam o que a tela
    // OFERECE (ex.: Mensal/Anual dos planos), onde o fundo sólido sozinho deixava a cor
    // como único canal. Só com `pill`, e não com `pillBgFromStyle`: o tingido é o
    // `--pill-bg`, que só sai do `--primary` sem ele.
    emphasizeActive: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingSkeletonsCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      primaryColor: '#000000',
    }
  },
  computed: {
    iconSize() {
      return this.small ? 16 : 18
    },
    activeIconColor() {
      return textOnColor(this.primaryColor)
    },
    inactiveIconColor() {
      return '#333333'
    },
    toggleClass() {
      return {
        'flat-gray': this.buttonStyle === 'flatGray',
        'flat-white': this.buttonStyle === 'flatWhite',
        'shadow-white': this.buttonStyle === 'shadowWhite',
        pill: this.pill,
        segmented: this.segmented,
        spaced: this.pill && this.spaced,
        'pill-bg-from-style': this.pill && this.pillBgFromStyle,
        'emphasize-active': this.pill && this.emphasizeActive,
        'toggle-small': this.small,
      }
    },
    // `aria-pressed` por opção, como mapa: o template lê uma propriedade em vez de chamar
    // função a cada render. Sem ele o leitor de tela não anuncia qual opção está ativa —
    // a cor do fundo é o único sinal, e ela não chega a quem não enxerga.
    ariaPressedPorValor() {
      const mapa = {}
      const opcoes = this.options || []
      opcoes.forEach((option) => {
        mapa[option.value] = this.value === option.value ? 'true' : 'false'
      })
      return mapa
    },
    buttonStyleBaseColor() {
      switch (this.buttonStyle) {
        case 'flatGray':
          return '#F0F1F3'
        case 'flatWhite':
        case 'shadowWhite':
        default:
          return '#FFFFFF'
      }
    },
    pillBackgroundColor() {
      if (!this.pill) return null

      if (this.pillBgFromStyle) {
        const [r, g, b] = hexToRgb(this.buttonStyleBaseColor)
        const factor = 0.96
        const dr = Math.round(r * factor)
        const dg = Math.round(g * factor)
        const db = Math.round(b * factor)
        return `rgb(${dr}, ${dg}, ${db})`
      }

      if (!this.primaryColor || this.primaryColor === '#000000') {
        return 'rgba(0, 0, 0, 0.15)'
      }
      const [r, g, b] = hexToRgb(this.primaryColor)
      return `rgba(${r}, ${g}, ${b}, 0.1)`
    },
    pillStyle() {
      if (!this.pill) return null
      return { '--pill-bg': this.pillBackgroundColor }
    },
    skeletonsCount() {
      if (this.loadingSkeletonsCount > 0) return this.loadingSkeletonsCount
      const optionsLength = (this.options || []).length
      return optionsLength > 0 ? optionsLength : 4
    },
    skeletonHeight() {
      // Não-small (pill ou não) é o mesmo --control-height (36px) do .v-btn real.
      if (this.small) return this.pill ? '32px' : '24px'
      return 'var(--control-height)'
    },
    skeletonWidth() {
      if (this.pill && this.small) return '110px'
      if (this.pill) return '130px'
      if (this.small) return '80px'
      return '120px'
    },
  },
  mounted() {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--primary')
      .trim()
    if (raw) this.primaryColor = raw
  },
  methods: {
    handleChange(value) {
      // Ao desmarcar, o Vuetify emite undefined — normaliza p/ manter o tipo do prop.
      const next = value === undefined ? '' : value
      this.$emit('input', next)
      this.$emit('change', next)
    },
    isActive(optionValue) {
      return this.value === optionValue
    },
    iconColor(optionValue) {
      return this.isActive(optionValue)
        ? this.activeIconColor
        : this.inactiveIconColor
    },
  },
}
</script>

<template>
  <div
    v-if="loading"
    class="toggle-button toggle-loading"
    :class="toggleClass"
    :style="pillStyle"
  >
    <BaseLoadingSkeleton
      v-for="i in skeletonsCount"
      :key="`toggle-skeleton-${i}`"
      class="toggle-skeleton-item"
      :width="skeletonWidth"
      :height="skeletonHeight"
    />
  </div>
  <!-- `role="group"` + `aria-pressed` por botão: o padrão ARIA de grupo de botões de
       alternância. O `aria-label` do consumidor chega a esta raiz via `$attrs`. -->
  <v-btn-toggle
    v-else
    :value="value"
    :style="pillStyle"
    :mandatory="!deselectable"
    dense
    borderless
    role="group"
    class="toggle-button"
    :class="toggleClass"
    @change="handleChange"
  >
    <template v-for="option in options">
      <v-tooltip
        v-if="option.icon && option.tooltip"
        :key="'tooltip-' + option.value"
        top
      >
        <!-- `v-bind="attrs"` além de `v-on="on"`: sem ele o `aria-describedby` do tooltip
             não chega ao botão, e numa opção só-ícone o tooltip é o ÚNICO nome dela
             (lacuna registrada em `design-system/guidelines.md`, corrigida ao estender). -->
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            :value="option.value"
            :class="toggleClass"
            :aria-pressed="ariaPressedPorValor[option.value]"
            v-bind="attrs"
            v-on="on"
          >
            <icon-svg
              :name="option.icon"
              :size="iconSize"
              :color="iconColor(option.value)"
            />
          </v-btn>
        </template>
        <span>{{ option.tooltip }}</span>
      </v-tooltip>

      <v-btn
        v-else
        :key="'btn-' + option.value"
        small
        :value="option.value"
        :class="toggleClass"
        :aria-pressed="ariaPressedPorValor[option.value]"
      >
        <icon-svg
          v-if="option.icon"
          :name="option.icon"
          :size="iconSize"
          :color="iconColor(option.value)"
        />
        <template v-else>
          {{ option.text }}
          <IconNoModuleAccess
            v-if="option.noAccessModule"
            iconSize="12"
            :module="option.noAccessModule"
            class="ml-2"
          />
        </template>
      </v-btn>
    </template>
  </v-btn-toggle>
</template>

<style lang="sass" scoped>
.toggle-button
  // DS Geist/shadcn: container nao-pilula (raio 8 via --radius-md).
  border-radius: var(--radius-md) !important
  // 36px no total: 26 (botoes flat) + 6 (padding 3px*2) + 4 (borda 2px*2).
  // Padding vertical 3px dá mais respiro Y ao botão (antes 2px); lateral 6px.
  padding: 3px 6px !important

  &.flat-gray
    background: #F0F1F3 !important
    border: 2px solid var(--primary-light) !important

  &.flat-white
    background: white !important
    border: 2px solid var(--primary-light) !important

  &.shadow-white
    background: white !important
    border: 2px solid var(--primary-light) !important

  // Variantes nao-pilula: botoes de 26px -> toggle de 36px (26 + 6 padding +
  // 4 borda), igual ao --control-height / BaseButton. Antes ficavam ~40px.
  &.flat-gray .v-btn,
  &.flat-white .v-btn,
  &.shadow-white .v-btn
    min-height: 26px !important
    max-height: 26px !important
    height: 26px !important

  &.pill
    background: var(--pill-bg) !important
    border: none !important
    padding: 0 !important
    box-shadow: none !important
    overflow: hidden

    .v-btn
      // Alinhado ao --control-height (36px) — igual às demais variantes.
      min-height: var(--control-height) !important
      max-height: var(--control-height) !important
      height: var(--control-height) !important
      font-size: 14px !important
      padding: 0 22px !important

    &.toggle-small
      padding: 0 !important

      .v-btn
        min-height: 32px !important
        max-height: 32px !important
        height: 32px !important
        font-size: 13px !important
        padding: 0 18px !important

  // Pílulas independentes com gap; o fundo dos inativos segue o buttonStyle.
  &.pill.spaced
    background: transparent !important
    overflow: visible !important
    display: inline-flex
    gap: 8px

    .v-btn:not(.v-btn--active)
      color: var(--black) !important

    &.flat-white .v-btn:not(.v-btn--active), &.shadow-white .v-btn:not(.v-btn--active)
      background: #FFFFFF !important

    &.flat-gray .v-btn:not(.v-btn--active)
      background: #F0F1F3 !important

    // Borda de controle (mesma do BaseButton `bordered`, ex: o botão "Filtros"):
    // sem ela a pílula branca se dilui no fundo claro da página.
    .v-btn
      border: 1px solid var(--border-input) !important

    // No ativo a borda fica transparente — o fundo primary pinta sob ela, então
    // os dois estados têm a mesma caixa e o hover acompanha sozinho. Precisa do
    // .v-btn--active para vencer o `border: none` do ativo lá embaixo, que
    // empata em especificidade e ganharia por vir depois.
    .v-btn.v-btn--active
      border-color: transparent !important

  .v-btn
    border: none !important
    // Item encaixa no container (raio levemente menor), igual ao segmented.
    border-radius: calc(var(--radius-md) - 2px) !important
    margin: 0 !important
    text-transform: none !important
    font-family: 'GeneralSans' !important
    font-size: 14px !important
    font-weight: 500 !important
    color: var(--black)
    min-width: 56px !important
    transition: all 0.2s ease !important
    min-height: 28px !important

    &.flat-gray
      background: #F0F1F3 !important

    &.flat-white
      background: white !important

    &.pill
      background: transparent !important
      box-shadow: none !important
      padding: 0 20px !important
      letter-spacing: 0 !important
      border-radius: calc(var(--radius-md) - 2px) !important
      color: var(--primary) !important
      font-weight: 500 !important

      &.pill-bg-from-style
        color: var(--black) !important

      &:hover:not(.v-btn--active)::before
        opacity: 0.04 !important

      &.v-btn--active
        background: var(--primary) !important
        background-color: var(--primary) !important
        color: var(--text-on-primary) !important
        box-shadow: none !important
        transform: none !important
        z-index: 1 !important

        // Vuetify aplica overlay 12% no ::before do active; removemos o pseudo
        // pra cor primary ficar 100% solida.
        &::before
          display: none !important

        &:hover
          background: var(--primary-light) !important
          background-color: var(--primary-light) !important
          color: var(--text-on-primary) !important

    &.v-btn--active
      background: var(--primary) !important
      background-color: var(--primary) !important
      border: none !important
      color: var(--text-on-primary) !important
      box-shadow: 0 2px 4px rgba(0,0,0,0.15) !important
      transform: scale(1.05) !important
      z-index: 1 !important

      &::before
        display: none !important

      &:hover
        background: var(--primary-light) !important
        background-color: var(--primary-light) !important
        color: var(--text-on-primary) !important

  &.toggle-small
    padding: 4px 6px !important

    .v-btn
      min-height: 20px !important
      max-height: 24px !important
      height: 24px !important
      min-width: 44px !important
      font-size: 12px !important
      padding: 0 12px !important

  // Variante "segmented" — replica EXATAMENTE o BaseSegmentedTabs (faixa cinza
  // --neutral-100 + pílula ativa branca com sombra). Sobrescreve o ativo padrão
  // (--primary) por causa da maior especificidade (&.segmented .v-btn...).
  &.segmented
    background: var(--neutral-100) !important
    border: none !important
    // $segmented-group-padding do BaseSegmentedTabs = 3px.
    padding: 3px !important
    border-radius: var(--radius-md) !important
    box-shadow: none !important

    .v-btn
      // Altura total do grupo = item + padding*2 = --control-height (36px).
      min-height: calc(var(--control-height) - 6px) !important
      max-height: calc(var(--control-height) - 6px) !important
      height: calc(var(--control-height) - 6px) !important
      padding: 0 var(--control-padding-x) !important
      // Item levemente menor que o grupo (encaixe), não-pílula.
      border-radius: calc(var(--radius-md) - 2px) !important
      background: transparent !important
      box-shadow: none !important
      font-family: 'Geist', sans-serif !important
      font-size: 13px !important
      font-weight: 500 !important
      color: var(--neutral-600) !important
      transform: none !important
      letter-spacing: 0 !important

      &:hover:not(.v-btn--active)
        color: var(--neutral-950) !important

      &:hover:not(.v-btn--active)::before
        opacity: 0 !important

      &.v-btn--active
        background: #fff !important
        background-color: #fff !important
        color: var(--neutral-950) !important
        font-weight: 600 !important
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12) !important
        transform: none !important
        z-index: 1 !important

        &::before
          display: none !important

        &:hover
          background: #fff !important
          background-color: #fff !important
          color: var(--neutral-950) !important

  // Variante `emphasizeActive` — o ativo SALTA: borda de 2px na cor da marca + fundo
  // tingido + sombra + peso 600, a mesma receita do card de plano selecionado. Os 2px
  // são 1px de borda + 1px de anel por box-shadow, para a BORDA não mudar a caixa no
  // clique. O peso 600 muda: alarga a pílula ativa em ~4px (medido: 91→95px no
  // "Mensal"), e é o preço do "peso maior" que o contrato pede. O texto vai para o preto
  // do DS porque o tingido é claro. Fica DEPOIS do bloco `pill.spaced`: a especificidade
  // empata com o `border-color: transparent` de lá, e a ordem desempata.
  &.pill.emphasize-active .v-btn.v-btn--active
    background: var(--pill-bg) !important
    background-color: var(--pill-bg) !important
    border: 1px solid var(--primary) !important
    box-shadow: 0 0 0 1px var(--primary), var(--shadow-sm) !important
    color: var(--neutral-950) !important
    font-weight: 600 !important

    &:hover
      background: var(--pill-bg) !important
      background-color: var(--pill-bg) !important
      color: var(--neutral-950) !important

  &.toggle-loading
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 6px
    padding: 6px 8px !important
    max-width: 100%
    overflow: visible !important
    background: transparent !important
    border: none !important

    .toggle-skeleton-item
      flex: 0 1 auto
      display: inline-block
      min-width: 0
      max-width: 100%

    ::v-deep .base-loading-skeleton
      border-radius: var(--radius-md) !important
      max-width: 100%
      overflow: hidden !important

      &::before
        border-radius: var(--radius-md) !important
        left: 0 !important
        top: 0 !important
</style>
